import React, { useCallback, useState } from 'react';
import TextField from '@atlaskit/textfield';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { FORMAT_STYLE_DECIMAL } from '../../types.tsx';
import { formatNumberField } from '../../index.tsx';
import type { Props } from './types.tsx';

export const FOCUS_BLUR_DEBOUNCE_TIME = 10;

export const FormattedNumberField = (props: Props) => {
	const { width = 'auto', value, formatConfig, ...rest } = props;

	const [editMode, setEditMode] = useState(false);
	const locale = useLocale();

	// on Chrome, when switching between types of inputs, there's an immediate extra blur and focus
	// which we want to avoid using a debounce so we can ensure correct error handling
	const [debouncedHandleFocusAndBlur] = useDebouncedCallback(() => {
		if (editMode) {
			props.onBlur?.();
		} else {
			props.onFocus?.();
		}
		setEditMode(!editMode);
	}, FOCUS_BLUR_DEBOUNCE_TIME);

	const formatNumber = useCallback(
		(newValue: string) => {
			// the browser should only provide valid numeric inputs, but double check to be safe
			const newNumber = Number(newValue);
			if (newValue && !Number.isNaN(newNumber)) {
				return formatNumberField(newNumber, {
					formatStyle: formatConfig.formatStyle ?? FORMAT_STYLE_DECIMAL,
					formatUnit: formatConfig.formatUnit,
					locale,
				});
			}
			return '';
		},
		[locale, formatConfig],
	);

	return (
		<TextField
			{...rest}
			width={width}
			type={editMode ? 'number' : 'text'}
			onFocus={debouncedHandleFocusAndBlur}
			onBlur={debouncedHandleFocusAndBlur}
			value={editMode ? value : formatNumber(value)}
			testId="platform-utils-number.ui.formatted-number-field.text-field"
		/>
	);
};
